import { lazy, Suspense } from "react";
import { FACE_BY_ID } from "~/lib/faces";
import { useMarkFaceVisited } from "~/lib/hooks/useMarkFaceVisited";
import { useFaceSeo } from "~/lib/seo/useSeo";

const AugmentStage = lazy(() =>
  import("~/three/AugmentStage").then((m) => ({ default: m.AugmentStage })),
);

export function Augment() {
  const face = FACE_BY_ID.augment;
  useMarkFaceVisited(face.id);
  useFaceSeo(face);

  return (
    <section className="flex w-full flex-col gap-10 pb-16">
      <div className="relative h-56 w-full overflow-hidden border-b border-jg-border/40 sm:h-72">
        <Suspense
          fallback={
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-b from-jg-augment/15 to-transparent"
            />
          }
        >
          <AugmentStage />
        </Suspense>

        <div className="pointer-events-none absolute right-4 top-4 sm:right-6">
          <span
            className="font-mono text-xs uppercase tracking-[0.25em]"
            style={{ color: face.themeColor }}
          >
            Cubie · {face.label}
          </span>
        </div>
      </div>

      <header className="mx-auto w-full max-w-4xl px-4">
        <h1 className="font-display text-3xl font-semibold leading-tight tracking-tight text-jg-fg sm:text-4xl">
          Step <span style={{ color: face.themeColor }}>through</span> the
          window
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-jg-fg-muted sm:text-base">
          AR &amp; VR experiences across Vuforia, AR Foundation, and Unity
          XR. Cross-platform pipelines and authoring tooling so content
          ships fast.
        </p>
      </header>

      <div className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
            <p
              className="font-mono text-[10px] uppercase tracking-[0.25em]"
              style={{ color: face.themeColor }}
            >
              What we build
            </p>
            <ul className="mt-3 space-y-2 text-sm text-jg-fg-muted">
              <li>
                <span className="text-jg-fg">Marker-based AR:</span> Vuforia,
                image-target experiences for marketing and education.
              </li>
              <li>
                <span className="text-jg-fg">SLAM / markerless:</span> AR
                Foundation (Unity), ARCore + ARKit unified pipeline.
              </li>
              <li>
                <span className="text-jg-fg">VR &amp; 3DOF:</span> Unity XR,
                Quest target, comfortable locomotion + UI patterns.
              </li>
              <li>
                <span className="text-jg-fg">Authoring tooling:</span> custom
                Unity editor extensions so non-engineers can ship content.
              </li>
            </ul>
          </div>

          <div className="rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
            <p
              className="font-mono text-[10px] uppercase tracking-[0.25em]"
              style={{ color: face.themeColor }}
            >
              Stack
            </p>
            <ul className="mt-3 flex flex-wrap gap-1.5">
              {[
                "Unity",
                "C#",
                "AR Foundation",
                "Vuforia",
                "ARCore",
                "ARKit",
                "Unity XR",
                "Shader Graph",
              ].map((t) => (
                <li
                  key={t}
                  className="rounded-full border border-jg-border bg-jg-bg/40 px-2.5 py-0.5 text-xs text-jg-fg-muted"
                >
                  {t}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs leading-relaxed text-jg-fg-muted">
              The portal above is a WebGL stand-in via{" "}
              <code className="rounded bg-jg-bg-soft/60 px-1 py-0.5">
                MeshPortalMaterial
              </code>
              . Real AR ships compiled to Android / iOS with the device
              camera as the backdrop.
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-dashed border-jg-border bg-jg-bg-soft/30 p-5 text-sm text-jg-fg-muted">
          <strong className="block font-display text-jg-fg">
            Shipped AR work
          </strong>
          <p className="mt-2">
            Past AR builds are under NDA or in pre-launch. Reach out via the
            Hire button for case-study references — we can walk through
            prior projects on a call once we know what you're building.
          </p>
        </div>
      </div>
    </section>
  );
}
